import { useState } from 'react';
import { Alert, Box, Button, FileInput, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type Props = {
  onPreviewDownload: () => Promise<void>;
  isPreviewDownloading: boolean;
  onDeployedDownload: () => Promise<void>;
  isDeployedDownloading: boolean;
  onUpload: (file: File) => Promise<void>;
  isUploading: boolean;
};

export default function DownloadUploadSection({
  onPreviewDownload,
  isPreviewDownloading,
  onDeployedDownload,
  isDeployedDownloading,
  onUpload,
  isUploading,
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const theme = useMantineTheme();

  const handleUpload = async () => {
    if (!file) return;
    try {
      await onUpload(file);
      notifications.show({
        title: 'Uploaded',
        message: 'export_assets.docx has been uploaded and will override the generated file.',
        color: 'green',
      });
      setFile(null);
    } catch {
      notifications.show({
        title: 'Upload failed',
        message: 'Failed to upload the file.',
        color: 'red',
      });
    }
  };

  const containerStyle = {
    borderRadius: 16,
    border: `1px solid ${theme.colors.gray[2]}`,
    overflow: 'hidden' as const,
  };

  return (
    <Stack gap="md">
      <Box p="md" style={containerStyle}>
        <Stack gap="sm">
          <Text fw={FONT_WEIGHTS.semibold}>Download Preview</Text>
          <Text size="sm" c="dimmed" style={{ whiteSpace: 'normal' }}>
            Generates a fresh export_assets.docx from your saved configuration so you can inspect it
            in Word. This does not deploy the file.
          </Text>
          <Button
            onClick={onPreviewDownload}
            loading={isPreviewDownloading}
            style={{ alignSelf: 'flex-start' }}
          >
            Download Preview
          </Button>
        </Stack>
      </Box>

      <Box p="md" style={containerStyle}>
        <Stack gap="sm">
          <Text fw={FONT_WEIGHTS.semibold}>Download Deployed File</Text>
          <Text size="sm" c="dimmed" style={{ whiteSpace: 'normal' }}>
            Downloads the currently deployed export_assets.docx from S3 (the file that is actually
            used for document exports).
          </Text>
          <Button
            onClick={onDeployedDownload}
            loading={isDeployedDownloading}
            variant="light"
            style={{ alignSelf: 'flex-start' }}
          >
            Download Deployed File
          </Button>
        </Stack>
      </Box>

      <Box p="md" style={containerStyle}>
        <Stack gap="sm">
          <Text fw={FONT_WEIGHTS.semibold}>Manual Upload Override</Text>
          <Alert color="yellow" variant="light">
            Uploading a file manually will override the deployed export_assets.docx. The saved
            configuration above will not reflect the manually uploaded file.
          </Alert>
          <FileInput
            label="Upload export_assets.docx"
            placeholder="Select .docx file"
            accept=".docx"
            value={file}
            onChange={setFile}
          />
          <Group>
            <Button
              onClick={handleUpload}
              loading={isUploading}
              disabled={!file}
              variant="light"
              color="orange"
            >
              Upload Override
            </Button>
          </Group>
        </Stack>
      </Box>
    </Stack>
  );
}
